import React, { useCallback } from "react";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import { useIntl } from "react-intl";

import Checkbox from "@app/component/input/Checkbox";
import NotificationSettingValue from "@app/types/notifications/NotificationSetting";

import styles from "../../NotificationsCommon.module.scss";
import Card from "../Card";
import Header from "../Header";

import getSharingUpdate from "./utils/getSharingUpdate";

interface Props {
  form: FormInstance<NotificationSettingValue>;
  onToggle: () => void;
}

const Sharing: React.FC<Props> = ({ form, onToggle }) => {
  const intl = useIntl();
  const handleToggleSharing = useCallback(
    (enabled: boolean) => {
      form.setFieldsValue({ sharing: getSharingUpdate(enabled) });
      onToggle();
    },
    [form, onToggle]
  );

  const handleChangeSetting = useCallback(
    (
        name:
          | "activityAssigned"
          | "emailTemplate"
          | "folder"
          | "groups"
          | "ownership"
          | "resource"
          | "routes"
          | "savedFilter"
      ) =>
      (enabled: boolean) => {
        form.setFieldsValue({ sharing: { [name]: enabled } });
      },
    [form]
  );

  return (
    <Card>
      <Form.Item hidden name="sharing">
        <div />
      </Form.Item>
      <Form.Item shouldUpdate={(prev, next) => prev.sharing.enabled !== next.sharing.enabled}>
        {({ getFieldValue }) => (
          <Header
            checked={getFieldValue(["sharing", "enabled"])}
            description={intl.formatMessage({
              id: "settings.notifications.sharing.text",
              defaultMessage:
                "Receive an email notification any time a record, group, or route is shared with you.",
              description: "Text of settings notifications Sharing card",
            })}
            onChange={handleToggleSharing}
            title={intl.formatMessage({
              id: "settings.notifications.sharing.title",
              defaultMessage: "Sharing",
              description: "Title of settings notifications Sharing card",
            })}
          />
        )}
      </Form.Item>

      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.sharing.enabled !== next.sharing.enabled ||
          prev.sharing.groups !== next.sharing.groups
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["sharing", "groups"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["sharing", "enabled"])}
            onChange={handleChangeSetting("groups")}
          >
            {intl.formatMessage({
              id: "settings.notifications.sharing.groups",
              defaultMessage: "Groups shared with me",
              description: "Settings notifications Sharing - Groups shared with me",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.sharing.enabled !== next.sharing.enabled ||
          prev.sharing.ownership !== next.sharing.ownership
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["sharing", "ownership"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["sharing", "enabled"])}
            onChange={handleChangeSetting("ownership")}
          >
            {intl.formatMessage({
              id: "settings.notifications.sharing.ownership",
              defaultMessage: "Ownership changed to me",
              description: "Settings notifications Sharing - Ownership changed to me",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.sharing.enabled !== next.sharing.enabled ||
          prev.sharing.routes !== next.sharing.routes
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["sharing", "routes"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["sharing", "enabled"])}
            onChange={handleChangeSetting("routes")}
          >
            {intl.formatMessage({
              id: "settings.notifications.sharing.routes",
              defaultMessage: "Routes shared with me",
              description: "Settings notifications Sharing - Routes shared with me",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.sharing.enabled !== next.sharing.enabled ||
          prev.sharing.activityAssigned !== next.sharing.activityAssigned
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["sharing", "activityAssigned"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["sharing", "enabled"])}
            onChange={handleChangeSetting("activityAssigned")}
          >
            {intl.formatMessage({
              id: "settings.notifications.sharing.activityAssigned",
              defaultMessage: "Activities assigned to me",
              description: "Settings notifications Sharing - Activities assigned to me",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.sharing.enabled !== next.sharing.enabled ||
          prev.sharing.emailTemplate !== next.sharing.emailTemplate
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["sharing", "emailTemplate"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["sharing", "enabled"])}
            onChange={handleChangeSetting("emailTemplate")}
          >
            {intl.formatMessage({
              id: "settings.notifications.sharing.emailTemplate",
              defaultMessage: "Email templates shared with me",
              description: "Settings notifications Sharing - Email templates shared with to me",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.sharing.enabled !== next.sharing.enabled ||
          prev.sharing.resource !== next.sharing.resource
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["sharing", "resource"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["sharing", "enabled"])}
            onChange={handleChangeSetting("resource")}
          >
            {intl.formatMessage({
              id: "settings.notifications.sharing.resource",
              defaultMessage: "Resources shared with me",
              description: "Settings notifications Sharing - Resources shared with me",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.sharing.enabled !== next.sharing.enabled ||
          prev.sharing.folder !== next.sharing.folder
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["sharing", "folder"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["sharing", "enabled"])}
            onChange={handleChangeSetting("folder")}
          >
            {intl.formatMessage({
              id: "settings.notifications.sharing.folder",
              defaultMessage: "Resource folders shared with me",
              description: "Settings notifications Sharing - Resource folders shared with me",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.sharing.enabled !== next.sharing.enabled ||
          prev.sharing.savedFilter !== next.sharing.savedFilter
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["sharing", "savedFilter"])}
            className={styles.secondaryCheckboxText}
            disabled={!getFieldValue(["sharing", "enabled"])}
            onChange={handleChangeSetting("savedFilter")}
          >
            {intl.formatMessage({
              id: "settings.notifications.sharing.savedFilter",
              defaultMessage: "Saved filters shared with me",
              description: "Settings notifications Sharing - Saved filters shared with me",
            })}
          </Checkbox>
        )}
      </Form.Item>
    </Card>
  );
};

export default Sharing;
